import { SeamRideUi } from './ui/seamRide';
import { watchContexts } from './render/contextGuard';
import './style.css';
// The UI system (tokens, shared frame, HUD layout); after style.css so it wins.
import './ui/theme.css';
// The front screens in ink wash (imported here, not by ui/front.ts, which the website shares).
// The in-game HUD (edge glass) and the windows over the world (ink-wash paper); after theme.css.
import './ui/hud-glass.css';
import './ui/panels-ink.css';
import {
  advanceTraining,
  growPlots,
  applyRealDay,
  DAY_SECONDS,
  offlineGain,
  duelMatchOf,
  setDuelV2,
  World,
  ZONES,
  type PlayerCommand,
} from '@nine/shared';
import { Sfx } from './audio';
import { sfxEngine } from './sfx';
import { tickSfx, withEventSfx } from './sfx/game';
import { music } from './music';
import { GamepadInput } from './gamepad';
import { Input } from './input';
import { Effects } from './render/effects';
import { Renderer } from './render/renderer';
import { ahead, appearanceOf, buildFigures, rigSet, rigStats } from './render/rig/sets';
import { clothWarmJobs, LOOKS, SpriteBank } from './render/sprites';
import { bakeStats, loadBakes } from './render/rig/bake';
import { frameCacheStats, loadFrameCache, releaseFrameCacheSoon } from './render/rig/frameCache';
import { IsoCamera } from './render/camera';
import { buildMinimap } from './render/terrain';
import { mountMenuIcons, setMenuIcon } from './render/menuIcons';
import { clearSave, loadCameraPrefs, loadSave, session, setSession, writeSave } from './storage';
import { newPlayer, skipOpening } from './newPlayer';
import './front/looks';
import { frontAudio } from './front/audio';
import { stage as frontStage } from './front/boot';
import { FRONT_DUO_F, FRONT_M } from './front/looks';
import { drawFrontPreviews, runFront } from './ui/frontPixel';
import { $ } from './ui/dom';
import { GmPanel } from './ui/gm';
import { EmoteMenu } from './ui/emotes';
import { DanceOverlay } from './ui/groupDance';
import { CarriageMenu } from './ui/carriage';
import { CombatBadge } from './ui/combatBadge';
import { DuelHud } from './ui/duelHud';
import { TourneyUi } from './ui/tourney';
import { WarehouseUi } from './ui/warehouse';
import { LockClient } from './ui/lockOn';
import { TradesMenu } from './ui/professions';
import { Fishing } from './ui/fishing';
import { Woodcut } from './ui/woodcut';
import { SectActivitiesUi } from './ui/sectActivities';
import { SettingsMenu } from './ui/settings';
import { idleScreen } from './ui/idleScreen.setting';
import { saveSettings, settings } from './settings';
import { onGraphicsChange, probeTier, startGraphics, watchFrames } from './graphics';
import { Hud } from './ui/hud';
import { Panels } from './ui/panels';
import { attachUiSounds } from './ui/uiSounds';
import { Checkout } from './ui/checkout';
import { MapView } from './ui/worldmap';
import { TodayPanel } from './ui/today';
import { installPhoneLayout } from './ui/phoneLayout';
import { installQiPool } from './ui/qiPool';
import { Notices } from './ui/notify';
import { FameWindow } from './ui/fame';
import { mountUis } from './ui/uis';
import { installQuestNav } from './ui/questNav';
import { Cutscene } from './ui/cutscene';
import { loadInkFonts } from './ui/inkFonts';
import { sandboxGateway } from './payment';
import { withServer } from './net/switch';
import { trialLogin, trialWanted } from './net/trial';
import { trackAssets } from './cache/loading';
import { loadChunk, onAppUpdate, onReloadNeeded, startCaching } from './cache/register';
import { render3dWanted } from './render3d/mode';
import { showLoadFailure } from './loading/failure';
import { nextFrame, Preloader, type StepDef } from './loading/loader';
import { LoadingScene, rememberDestination } from './loading/scene';
import { applyRestFace } from './render/rig/b3/faceExprWire';
import {
  BOOT_STEPS,
  FRONT_STEPS,
  loadFonts,
  warmFront,
  warmWorld,
  WORLD_STEPS,
  type BootStep,
  type FrontStep,
  type WorldStep,
} from './loading/warmup';

// The duel (docs/research/C0_PVP.md): rivals with their own builds, guard points, counter stances, lock-on, tells and
// the ring (data/duelMatch.ts). Against a server the zone has no stand-in rival: the other tab is the opponent.
setDuelV2(true, !new URLSearchParams(location.search).has('server') && !trialWanted());

const STEP = 1 / 60;

const AUTOSAVE_SECONDS = 10;

/**
 * Login/title screen skipped for now (user, 2026-10-08): boot straight into the world with the
 * saved character, else a new default one. Set to `false` (or open `?front=1`) to bring it back.
 */
const SKIP_FRONT = false;

async function boot(scene: LoadingScene): Promise<void> {
  if (SKIP_FRONT && !new URLSearchParams(location.search).has('front')) {
    const at = session().slot;
    const had = loadSave(at);
    if (!had) writeSave(newPlayer(), at);
    // The first boot of a browser session starts at the plains hub (user, 2026-10-09); zone changes
    // later in the session reload the page and keep where they went. `?hub=1` goes to the hub on any boot.
    else if (!sessionStorage.getItem('c0-started') || new URLSearchParams(location.search).has('hub')) {
      had.location = 'plains';
      had.arriveFrom = null;
      had.indoors = null;
      // Saves made before the opening was skipped (newPlayer.ts) land in play too.
      writeSave(skipOpening(had), at);
    }
    try {
      sessionStorage.setItem('c0-started', '1');
    } catch {
      // Private mode: every boot starts at the hub.
    }
    if (!session().user || !session().enter) setSession({ user: session().user || 'ผู้เยี่ยมชม', enter: true });
  }
  // The open online trial (net/trial.ts): a guest login first, then the server's character instead of a local save.
  const trial = await trialLogin();
  // Chose to play offline in a trial build: the duel's stand-in rival stays, as in a normal build.
  if (!trial) setDuelV2(true, !new URLSearchParams(location.search).has('server'));
  const slot = session().slot;
  const saved = trial?.progress ?? loadSave(slot);
  // Straight into the world (a zone change, a character just picked): the world loads behind this
  // same scene. Else the login and character select come first, and the world loads on entering.
  const straightIn = !!(session().user && session().enter && saved);
  const plan = { ...BOOT_STEPS, ...(straightIn ? WORLD_STEPS : FRONT_STEPS) };
  const load = new Preloader(plan as Record<BootStep | WorldStep | FrontStep, StepDef>, scene);
  load.mark('code', 0);
  const fonts = load.optional('fonts', loadFonts);
  // The windows' serif and brush faces: ready before the world shows (ui/inkFonts.ts).
  const inkFonts = loadInkFonts();
  const worldAt = performance.now();
  const progress = saved ?? newPlayer();
  const away = saved ? (Date.now() - saved.savedAt) / 1000 : 0;
  const offline = offlineGain(away);
  progress.cultivation += offline;
  // Training runs on real time, so it carries on while the game is closed.
  const trainedAway = advanceTraining(progress, away);
  // Crops in the fields grow on while the game is closed.
  growPlots(progress.trades, away);
  const trainCostAway = trainedAway.charges.reduce((a, b) => a + b, 0);

  // GM menu: always in dev builds, or on demand with ?gm in the URL while the game is in development.
  const gmMode = import.meta.env.DEV || new URLSearchParams(location.search).has('gm');
  // The world's day follows the real clock, so it carries on across zones and visits.
  const hour = ((Date.now() / 1000) % DAY_SECONDS) / (DAY_SECONDS / 24);
  // `story`: the opening and the story's cutscenes play (ui/cutscene.ts draws them).
  // Dev only: `?server=ws://…` plays against a zone server instead (net/switch.ts); local otherwise.
  const world = withServer(new World({ progress, gm: gmMode, hour, story: true }), trial);
  // The real day (05:00 reset) for the small dungeons' daily reward and the training boost; re-pinned once a minute below.
  applyRealDay(world, Date.now());
  let dayT = 60;
  // The player drawn as Model 0 baked to sprites (lane C0-M0) only with `?m0=1`; the old sprite is the default.
  // The figure frames this browser kept from an earlier load (render/rig/frameCache.ts), before any is asked for.
  await frames;
  // The player's resting look (appearance.face) on their looks before any frame is drawn (lane FACE-EXPR).
  applyRestFace(LOOKS, progress.appearance.face);
  const sprites = await (await import('./render/m0')).m0Sprites(new SpriteBank(buildFigures(), (look) => rigSet(appearanceOf(look))), progress.appearance.sex);
  // The front's heroes (a man and a woman) stand in the picture; their frames are asked for ahead.
  {
    const duo = { m: { ...FRONT_M, outfit: [...FRONT_M.outfit] }, f: { ...FRONT_DUO_F, outfit: [...FRONT_DUO_F.outfit] } };
    frontStage?.setDuo(sprites, duo);
  }
  load.mark('world', worldAt);
  if (straightIn) scene.destination(scene.dest ?? `กำลังเข้าสู่${ZONES[world.map.zone].name}`);
  // Baked frames for the people here (npm run bake), loaded behind the loading screen so they
  // show from the first frame. Anyone dressed in an outfit is drawn as before.
  const here = new Set([...world.entities.values()].filter((e) => !e.outfit?.length).map((e) => e.sprite));
  // Through the cache in this browser (cache/): the scene says whether they came from it.
  await load.optional('bakes', (report) =>
    trackAssets(report, scene, () =>
      loadBakes([...here].flatMap((s) => (LOOKS[s] ? [appearanceOf(LOOKS[s]).key ?? ''] : []))),
    ),
  );
  const sfx = new Sfx();
  frontAudio.bind(
    sfx,
    () => {
      settings.muted = !settings.muted;
      saveSettings();
      sfx.muted = settings.muted;
      sfx.applyLevels();
      return settings.muted;
    },
    frontStage,
  );
  if (frontStage) frontStage.onScene = (id) => frontAudio.scene(id);
  const view = $<HTMLCanvasElement>('#view');
  const hurt = $('#hurt');
  const iso = new IsoCamera(world.map);
  const camPrefs = loadCameraPrefs();
  const effects = new Effects(world, iso, sprites, sfx, () => {
    hurt.style.opacity = '0.55';
    setTimeout(() => (hurt.style.opacity = '0'), 120);
  });
  const renderer = await load.run('gpu', () => Renderer.create(view, world, sprites, effects, iso));
  const uiAt = performance.now();
  renderer.setZoom(camPrefs.zoom);
  renderer.setAngle(camPrefs.angle ?? camPrefs.rot);
  // The 3D renderer (?render=3d or the render3d setting; docs/archive/3D.md): three.js in its own chunk,
  // loaded only when it is on. It draws outdoors and hands back to the 2.5D renderer indoors.
  const want3d = render3dWanted();
  // PROTOTYPE (?camera=free): the world in low-resolution 3D under a free orbit camera, outdoors.
  // Never beside the 3D renderer: both would take over picking and the screen.
  // C0-ROTATE: the camera turns freely by default in the painted renderer (render/turned/); the old
  // low-poly prototype only on an explicit `?camera=free`.
  const oldFree = new URLSearchParams(location.search).get('camera') === 'free';
  if (want3d && oldFree) console.warn('?camera=free is off while the 3D renderer (render3d) is on');
  // A chunk that cannot be loaded (an update replaced it) leaves the 2.5D renderer, and a toast.
  const world3d =
    oldFree && !want3d
      ? ((await loadChunk(() => import('./render/world3d'), 'the 3D prototype'))?.World3D.create(
          view,
          world,
          sprites,
          renderer,
          iso,
        ) ?? null)
      : null;
  const view3d = want3d
    ? ((await loadChunk(() => import('./render3d'), 'the 3D renderer'))?.View3D.create({
        view,
        world,
        sprites,
        effects,
        renderer,
        iso,
      }) ?? null)
    : null;
  // Dev-only handle for poking the simulation from the console or test drivers.
  if (import.meta.env.DEV)
    Object.assign(window, {
      __world: world,
      __iso: iso,
      __fx: effects,
      __sfx: sfx,
      __sfxEngine: sfxEngine,
      __renderer: renderer,
      __w3d: world3d,
      __view3d: view3d,
      __sprites: sprites,
      __rigStats: rigStats,
      __bakeStats: bakeStats,
      __frameCache: frameCacheStats,
      __load: load,
      // QA helper (headless runs cannot click the challenge card): puts the hero beside the hub rival and starts a
      // bout; `botLevel` (0..) sets the rival's skill for the next style. Returns whether a bout started.
      __ns: {
        startDuel(botLevel = 0): boolean {
          const dm = duelMatchOf(world);
          const r = dm ? world.get(dm.rivalId) : undefined;
          if (!dm || !r) return false;
          const lv = (dm as unknown as { levels: Record<string, number> }).levels;
          lv[dm.nextStyle.id] = botLevel;
          const p = world.player;
          p.x = r.x - 1.2;
          p.y = r.y;
          p.combatT = 0;
          world.apply({ t: 'duelChallenge', id: r.id });
          return dm.active;
        },
      },
    });

  let started = false;
  // The world is drawn once it is being entered (behind the loading scene, loading/warmup.ts), not
  // behind the login and character select.
  let drawWorld = false;
  const send = (c: PlayerCommand) => {
    if (started) world.apply(c);
  };
  let input: Input | null = null;
  let gamepad: GamepadInput | null = null;
  document.querySelector('#zonecard .mapname')!.textContent = ZONES[world.map.zone].name;
  const minimapBase = buildMinimap(world.map);
  const hud = new Hud(world, sprites, minimapBase, {
    command: send,
    setBlock: (on) => input?.setTouchBlock(on),
    dash: () => input?.dash(),
    useSlot: (slot) => (input ? input.useSlot(slot) : send({ t: 'skill', slot })),
    openPanel: (tab) => panels.open(tab),
  });
  installQuestNav(world, send);
  onAppUpdate(() => hud.toast('มีเกมเวอร์ชันใหม่แล้ว จะใช้เมื่อเปิดเกมครั้งถัดไป'));
  onReloadNeeded(() => hud.toast('โหลดบางส่วนของเกมไม่สำเร็จ อาจมีเวอร์ชันใหม่ · โปรดโหลดหน้าใหม่'));
  // Payments credit the jade straight away and save, so a paid top-up is never lost to a crash.
  const checkout = new Checkout(sandboxGateway, (r) => {
    const ok = world.creditTopUp(r);
    if (ok) save();
    return ok;
  });
  const panels = new Panels(world, send, sprites, checkout);
  // Cutscenes: drawn from world.scene; the player only advances or skips them.
  const cutscene = new Cutscene(
    world,
    renderer,
    sprites,
    sfx,
    send,
    () => started,
    () => {
      hud.closeDialog();
      panels.close();
    },
    // The "go here" marker steps aside while a window is open.
    () =>
      !!panels.tab ||
      mapView.isOpen ||
      settingsMenu.open ||
      hud.menuOpen ||
      checkout.isOpen ||
      emoteMenu.isOpen ||
      tradesMenu.isOpen,
  );
  const mapView = new MapView(world, minimapBase, send);
  const gmPanel = gmMode
    ? new GmPanel(world, send, {
        save: () => {
          writeSave(world.progress);
          hud.toast('GM: บันทึกแล้ว');
        },
        wipeSave: () => {
          clearSave();
          location.reload();
        },
      })
    : null;
  if (gmPanel) {
    const mb = document.createElement('button');
    mb.type = 'button';
    mb.className = 'gm-btn';
    mb.append(Object.assign(document.createElement('i'), { className: 'gm-mark', textContent: '令' }));
    mb.append(Object.assign(document.createElement('span'), { textContent: 'GM' }));
    mb.append(Object.assign(document.createElement('kbd'), { textContent: '`' }));
    mb.addEventListener('click', () => gmPanel.toggle());
    $('#gmSys').append(mb);
    addEventListener('keydown', (e) => {
      if (e.code === 'Backquote' && !(e.target as HTMLElement | null)?.closest?.('input, textarea, select')) {
        e.preventDefault();
        gmPanel.toggle();
      }
    });
  }
  input = new Input(view, world, view3d ?? renderer, hud, panels, send, () => sfx.unlock());
  // The minimap turns with the camera (render/turnView.ts).
  renderer.turn.onAngle = (a) => {
    hud.setMapRotation(a);
    // C0-ROTATE: a held walk follows the camera (keys are screen directions).
    input?.cameraTurned();
  };
  if (view3d) view3d.onAngle = (a) => hud.setMapRotation(a);
  hud.setMapRotation(renderer.turn.angle);
  addEventListener('resize', () => renderer.resize());
  for (const [id, act] of [
    ['#zoomOut', () => input?.zoomCamera(-1)],
    ['#zoomIn', () => input?.zoomCamera(1)],
  ] as const)
    $(id).addEventListener('click', (event) => {
      event.stopPropagation();
      act();
    });

  for (const [id, tab] of [
    ['#mbChar', 'char'],
    ['#mbBag', 'bag'],
    ['#mbArts', 'arts'],
    ['#mbNeigong', 'neigong'],
    ['#mbSchool', 'school'],
    ['#mbSkin', 'skin'],
    ['#mbMount', 'mount'],
    ['#mbMall', 'mall'],
    ['#mbPass', 'pass'],
    ['#mbLadder', 'ladder'],
    ['#mbArena', 'arena'],
    ['#mbHelp', 'help'],
  ] as const)
    $(id).addEventListener('click', () => panels.toggle(tab));
  $('#trAuto').addEventListener('click', () => send({ t: 'trainAuto', on: !world.progress.trainAuto }));
  mountMenuIcons($('#hud'));
  const mute = $('#mute');
  const applyAudio = () => {
    sfx.volume = settings.volume;
    sfx.music = settings.music;
    sfx.muted = settings.muted;
    sfx.applyLevels();
    mute.querySelector('span')!.textContent = `เสียง: ${sfx.muted ? 'ปิด' : 'เปิด'}`;
    mute.setAttribute('aria-pressed', String(sfx.muted));
    setMenuIcon(mute.querySelector('i')!, sfx.muted ? 'soundOff' : 'soundOn');
    // Frosted glass under the HUD and the windows, or plain see-through fills (ui/blur.setting.ts).
    document.documentElement.dataset.blur = settings.uiBlur ? 'on' : 'off';
  };
  applyAudio();
  // A graphics tier picked, or lowered by the first run's probe (graphics.ts): its sharpness and blur at once.
  onGraphicsChange(() => {
    renderer.resize();
    applyAudio();
  });
  mute.addEventListener('click', () => {
    settings.muted = !settings.muted;
    saveSettings();
    applyAudio();
  });
  const leaveWorld = (patch: Partial<ReturnType<typeof session>>) => {
    input!.enabled = false;
    writeSave(world.progress);
    setSession(patch);
    location.reload();
  };
  const settingsMenu = new SettingsMenu({
    applyAudio,
    zoom: (n) => input?.zoomCamera(n),
    openHelp: () => panels.open('help'),
    save: () => {
      writeSave(world.progress);
      hud.toast('บันทึกเกมแล้ว');
    },
    // Leaving the world: save, then reload into the front screens. The reload is the teardown: the
    // sim loop, the audio context and a `?server=` socket all end with the page; the front then
    // opens on the character select (user + server kept) or the login (both cleared), ui/frontPixel.ts.
    quitToTitle: () => leaveWorld({ enter: false }),
    logout: () => leaveWorld({ enter: false, user: null, server: null }),
  });
  // Esc: close whatever window is open; with none open, toggle the HUD's menu (เมนู Esc).
  input.onMap = () => mapView.toggle();
  const emoteMenu = new EmoteMenu(world, sprites, send);
  input.onEmote = () => emoteMenu.toggle();
  const danceOverlay = new DanceOverlay(world, sprites, send);
  const carriageMenu = new CarriageMenu(world, sprites, send);
  const seamRide = new SeamRideUi(world);
  const combatBadge = new CombatBadge();
  const duelHud = new DuelHud(world, send, effects, (t) => hud.toast(t));
  const tourneyUi = new TourneyUi(world, send, (t) => hud.toast(t));
  const warehouseUi = new WarehouseUi(world, send);
  // The persistent lock-on: Tab or a click locks, A/D circle, W/S close in and back off (ui/lockOn.ts).
  const lockClient = new LockClient(world, send);
  input.lock = lockClient;
  lockClient.onChange = () => input.refreshMove();
  let lockRefresh = 0;
  const tradesMenu = new TradesMenu(world, send);
  input.onTrades = () => tradesMenu.toggle();
  const todayPanel = new TodayPanel(world, send);
  installQiPool(world, send);
  const notices = new Notices(world);
  // Windows and HUD pieces that register themselves (ui/*.ui.ts, ui/registry.ts).
  const uis = mountUis({ world, send, toast: (t) => hud.toast(t) });
  input.onToday = () => todayPanel.toggle();
  installPhoneLayout({
    world,
    send,
    setBlock: (on) => input?.setTouchBlock(on),
    dash: () => input?.dash(),
    attack: () => (input ? input.useSlot(0) : send({ t: 'skill', slot: 0 })),
    openToday: () => todayPanel.open(),
    openMap: () => mapView.open(),
    openBag: () => panels.open('bag'),
    toggleMenu: () => hud.toggleMenu(),
  });
  const fameWin = new FameWindow(world);
  input.onFame = () => fameWin.toggle();
  const fishing = new Fishing(world, send);
  const woodcut = new Woodcut(world, send);
  const sectUi = new SectActivitiesUi(world, send);
  input.onWork = () => fishing.key() || woodcut.key() || tradesMenu.workHere();
  gamepad = new GamepadInput(
    input,
    hud,
    send,
    () => sfx.unlock(),
    () => mapView.toggle(),
  );
  $('#mapclip').addEventListener('click', () => mapView.open());
  $('#mbMap').addEventListener('click', () => mapView.toggle());
  input.onEscape = () => {
    if (hud.menuOpen) return hud.closeMenu();
    if (uis.some((u) => u.escape?.())) return;
    if (emoteMenu.isOpen) return emoteMenu.close();
    if (tradesMenu.isOpen) return tradesMenu.close();
    if (todayPanel.isOpen) return todayPanel.close();
    if (fameWin.isOpen) return fameWin.close();
    if (mapView.isOpen) return mapView.close();
    if (settingsMenu.open) return settingsMenu.close();
    if (checkout.isOpen) return checkout.close();
    if (!$('#dialog').hidden || panels.tab) {
      hud.closeDialog();
      panels.close();
      return;
    }
    if (gmPanel?.isOpen) return gmPanel.toggle();
    // With nothing open, Esc first lets go of the target, then opens the menu.
    if (world.get(world.player.targetId)) return send({ t: 'untarget' });
    hud.toggleMenu();
  };
  // The interface's own sounds: windows, the menu, tabs, buttons, chat, jade (ui/uiSounds.ts).
  attachUiSounds(world);
  $('#mbSettings').addEventListener('click', () => settingsMenu.toggle());
  for (const id of ['#pkBtn', '#autoBtn']) $(id).addEventListener('pointerdown', (e) => e.stopPropagation());
  $('#pkBtn').addEventListener('click', () => send({ t: 'pk', on: !world.player.pk }));
  $('#autoBtn').addEventListener('click', () => hud.toggleAuto());
  const fpsEl = $('#fps');
  let fpsT = 0;
  let fpsN = 0;
  let profileN = 0,
    profileT = 0,
    simMs = 0,
    renderMs = 0,
    uiMs = 0;

  // The arena's day follows the local calendar (the server's, once online).
  const newDay = () => {
    const d = new Date();
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const paid = world.arenaNewDay(key);
    if (paid) hud.banner('ประลองยุทธปิดวัน · รับรางวัลแล้ว');
  };
  const save = () => {
    if (started) writeSave(world.progress);
  };
  addEventListener('pagehide', save);
  watchContexts({ view, toast: (t) => hud.toast(t), save });
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && save());

  // Splash
  const btnPlay = $<HTMLButtonElement>('#btnPlay');
  const btnNew = $<HTMLButtonElement>('#btnNew');
  btnPlay.textContent = saved ? 'เล่นต่อ' : 'เข้าสู่ยุทธภพ';
  btnNew.hidden = !saved;
  if (saved && offline > 0)
    $('#offline').textContent = `ระหว่างที่คุณไม่อยู่ ฝึกปรือได้ ${offline.toLocaleString('th-TH')} แต้ม`;
  let confirmNew = false;
  btnNew.addEventListener('click', () => {
    if (!confirmNew) {
      confirmNew = true;
      btnNew.textContent = 'ยืนยัน ลบเซฟแล้วเริ่มใหม่';
      return;
    }
    clearSave();
    location.reload();
  });
  const start = () => {
    if (started) return;
    started = true;
    input!.enabled = true;
    sfx.unlock();
    music.release();
    $('#splash').hidden = true;
    hud.chat('sys', 'ยินดีต้อนรับสู่ยุทธภพ เก้ากระบี่ออนไลน์');
    if (offline > 0) hud.chat('sys', `ฝึกปรือระหว่างออฟไลน์ได้ ${offline.toLocaleString('th-TH')} แต้ม`);
    if (trainCostAway > 0)
      hud.chat('sys', `ระหว่างที่ไม่อยู่ สูญเสียค่าฝึกฝน ${trainCostAway.toLocaleString('th-TH')} หน่วย`);
    const lastAway = trainedAway.done.at(-1);
    if (lastAway)
      hud.chat('sys', `ระหว่างที่ไม่อยู่ ฝึก${world.trainingName(lastAway)}สำเร็จถึงขั้น ${lastAway.level}`);
    if (trainedAway.starved) hud.chat('sys', 'แต้มฝึกปรือไม่พอจ่ายค่าฝึก การฝึกหยุดรอ');
    if (!saved) hud.toast('คลิกผู้เฒ่าหลิว (!) เพื่อรับภารกิจแรก · กด H ดูวิธีเล่น');
    // Slow frames early on bring a suggestion to lower the graphics tier, never a change (graphics.ts).
    watchFrames((text) => hud.toast(text));
    newDay();
    writeSave(world.progress);
  };
  btnPlay.addEventListener('click', start);
  load.mark('ui', uiAt);

  // The player's figure goes ahead of everyone else's: its frames are drawn first, its everyday
  // poses in every view before they are needed (sets.ts), and its streaming-cloth frames in idle
  // time; all again whenever their outfit changes.
  let warmJobs: (() => void)[] = [];
  let warmedSprite = '';
  const warm = () => {
    const set = sprites.of(world.player);
    const sprite = `${world.player.sprite}|${world.player.outfit.join(',')}`;
    if (set) {
      set.focus?.();
      // The figure with the weapon put away (or drawn) is a set of its own, shown after Z and for
      // struck emotes: ready too, after the figure in use.
      const p = world.player;
      const other = p.outfit.includes('sheathed')
        ? p.outfit.filter((o) => o !== 'sheathed')
        : [...p.outfit, 'sheathed'];
      sprites.of({ sprite: p.sprite, outfit: other, id: p.id })?.focus?.(false);
    }
    if (sprite !== warmedSprite && set) {
      warmedSprite = sprite;
      warmJobs = ahead(() => clothWarmJobs(set));
    }
    const end = performance.now() + 6;
    ahead(() => {
      while (warmJobs.length && performance.now() < end) warmJobs.pop()!();
    });
    setTimeout(warm, warmJobs.length ? 30 : 100);
  };

  // Fixed-step simulation, variable-rate rendering.
  let last = performance.now();
  let acc = 0;
  let saveT = AUTOSAVE_SECONDS;
  const frame = (now: number) => {
    // rAF timestamps can precede the performance.now() taken at boot, so clamp at 0.
    const elapsed = Math.max(0, (now - last) / 1000);
    const dt = Math.min(0.1, elapsed);
    last = now;
    const profileStart = performance.now();
    acc += dt * (gmPanel?.timeScale ?? 1);
    if ((dayT -= dt) <= 0) {
      dayT = 60;
      applyRealDay(world, Date.now());
    }
    while (acc >= STEP) {
      if (started) world.step(STEP);
      else world.time += STEP; // idle animation behind the splash
      for (const ev of world.drainEvents()) {
        if (ev.type === 'travel') {
          // Off to another zone: save, then load it (through the loading screen).
          writeSave(world.progress);
          setSession({ enter: true });
          const dest = `กำลังเดินทางไป${ZONES[ev.to].name}…`;
          rememberDestination(dest);
          scene.show(dest);
          setTimeout(() => location.reload(), 600);
        }
        withEventSfx(ev, effects, () => effects.handle(ev));
        music.handle(ev, world);
        cutscene.handle(ev);
        hud.handle(ev);
        fishing.handle(ev);
        woodcut.handle(ev);
        sectUi.handle(ev);
        danceOverlay.handle(ev);
        carriageMenu.handle(ev);
        duelHud.handle(ev);
        tourneyUi.handle(ev);
        for (const u of uis) u.handle?.(ev);
        warehouseUi.handle(ev);
      }
      acc -= STEP;
    }
    if (!started) {
      for (const e of world.entities.values()) e.animT += dt;
      drawFrontPreviews(sprites, dt);
    }
    // The music for the moment (music/context.ts), or the cutscene's own.
    if (started) music.update(world, cutscene.music);
    // While a scene plays the gamepad only answers it (ui/cutscene.ts).
    if (!cutscene.active) gamepad?.poll();
    cutscene.sync();
    effects.update(dt);
    tickSfx(dt, effects, started);
    const renderStart = performance.now();
    idleScreen.update(world, dt);
    if ((started || drawWorld) && !idleScreen.active && !view3d?.draw(dt)) {
      const in3d = !!world3d && !world.map.interior && !renderer.stage;
      if (world3d) world3d.shown = in3d;
      if (in3d) world3d!.render(dt);
      else renderer.render(dt);
    }
    const uiStart = performance.now();
    hud.sync(dt);
    danceOverlay.sync(dt);
    carriageMenu.sync();
    seamRide.sync();
    duelHud.sync(dt);
    combatBadge.sync(world);
    tourneyUi.sync(dt);
    warehouseUi.sync();
    lockClient.sync(dt);
    if ((lockRefresh += dt) > 0.1) {
      lockRefresh = 0;
      input.refreshMove();
    }
    mapView.sync(dt);
    todayPanel.sync(dt);
    notices.sync(dt);
    fameWin.sync(dt);
    tradesMenu.sync();
    fishing.sync(dt);
    woodcut.sync(dt);
    sectUi.sync(dt);
    for (const u of uis) u.sync?.(dt);
    gmPanel?.sync(elapsed);
    fpsEl.hidden = !settings.showFps;
    if (settings.showFps && (fpsT += elapsed) >= 0.5) {
      fpsEl.textContent = `${Math.round(++fpsN / fpsT)} FPS · WebGL`;
      fpsT = 0;
      fpsN = 0;
    } else fpsN++;
    panels.refresh();
    simMs += renderStart - profileStart;
    renderMs += uiStart - renderStart;
    uiMs += performance.now() - uiStart;
    profileN++;
    if ((profileT += elapsed) >= 0.5) {
      view.dataset.frameProfile = JSON.stringify({
        simulation: +(simMs / profileN).toFixed(2),
        render: +(renderMs / profileN).toFixed(2),
        ui: +(uiMs / profileN).toFixed(2),
        entities: world.entities.size,
        stages: renderer.profile,
        ...(view3d?.shown ? { render3d: view3d.profile } : {}),
      });
      profileN = profileT = simMs = renderMs = uiMs = 0;
    }
    if (started && (saveT -= dt) <= 0) {
      saveT = AUTOSAVE_SECONDS;
      newDay();
      save();
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  // The game screen shows only once everything it needs is loaded (loading/warmup.ts): straight
  // in behind this scene, or from the character select through the scene again.
  const hooks = { world, sprites, renderer, panels, mapView, drawWorld: (on: boolean) => (drawWorld = on) };
  let entering: Promise<void> | null = null;
  const enterWorld = (planned?: Pick<Preloader<WorldStep>, 'run'>) =>
    (entering ??= (async () => {
      // Picked on the character select: a click, so the sound can start now.
      if (!planned) sfx.unlock();
      music.play('loading');
      const into = planned ?? new Preloader(WORLD_STEPS, scene);
      if (!planned) scene.show(`กำลังเข้าสู่${ZONES[world.map.zone].name}`, false);
      await Promise.all([fonts, inkFonts]);
      try {
        warm();
      } catch (error) {
        // The player's cloth warm-up is a nicety: a fault there must not keep them out of the world.
        console.error('Player warm-up failed', error);
      }
      await warmWorld(hooks, into);
      // The first run's check of the guessed graphics tier, still behind the loading scene (graphics.ts).
      await probeTier();
      start();
      releaseFrameCacheSoon();
      await scene.hide();
    })().catch((error: unknown) => {
      console.error('Entering the world failed', error);
      showLoadFailure(scene, error, 'enter');
    }));
  await fonts;
  // The front screens play the login track; the first click or key on them unlocks the sound.
  if (!straightIn || !entering) {
    frontAudio.playFront();
    const front = document.getElementById('front');
    const unlock = () => music.unlock();
    front?.addEventListener('pointerdown', unlock, { once: true });
    front?.addEventListener('keydown', unlock, { once: true });
  }
  if (straightIn) {
    runFront({ sprites, loadedSlot: slot, start: () => void enterWorld(load) });
    // The character select after all (the save was not there): it shows once this is done.
    if (!entering) await scene.hide();
  } else {
    await load.run('front', (report) => warmFront(sprites, report));
    runFront({ sprites, loadedSlot: slot, start: () => void enterWorld() });
    await nextFrame();
    await scene.hide();
  }
}

// The graphics tier (and with it the characters' pixel density), before anything is drawn.
startGraphics();
// The service worker and the cache of large files (cache/register.ts).
startCaching();
// Figure frames kept from an earlier load, read while the code and fonts come in (after the density is set).
const frames = loadFrameCache();
// Let the loading screen paint before the heavy work of building every sprite.
const scene = new LoadingScene();
requestAnimationFrame(() =>
  setTimeout(() => {
    void boot(scene).catch((error: unknown) => {
      console.error('Game boot failed', error);
      // A download that failed can be tried again, the GPU's cannot; the step and file as details.
      showLoadFailure(scene, error, 'boot');
    });
  }, 30),
);
